import { z } from 'zod';
import { STATUS } from './entities.js';
import { KEY_REGEX } from './fields.js';
export const LIFECYCLE_POLICY_ENTITIES = ['quotes', 'jobs', 'appointments', 'invoices'];
export const LIFECYCLE_POLICY_MODES = ['enforce', 'observe'];
const fieldPath = z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, 'Use a column or custom-field path such as service_location_id or fields.customer_signed.')
    .max(120);
export const LifecyclePolicyCondition = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('total'),
        min: z.number().nonnegative().optional(),
        max: z.number().nonnegative().optional(),
    })
        .refine((value) => value.min !== undefined || value.max !== undefined, {
        message: 'A total condition needs min or max.',
    }),
    z.object({
        kind: z.literal('field_equals'),
        path: fieldPath,
        value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    }),
]);
export const LifecyclePolicyRequirement = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('field_present'),
        path: fieldPath,
        label: z.string().min(1).max(160),
    }),
    z.object({
        kind: z.literal('document_count'),
        document_kind: z.string().min(1).max(60).optional(),
        min: z.number().int().min(1).max(20).default(1),
        label: z.string().min(1).max(160),
    }),
    z.object({
        kind: z.literal('appointment_count'),
        statuses: z.array(z.enum(STATUS.appointments)).min(1).optional(),
        min: z.number().int().min(1).max(50).default(1),
        label: z.string().min(1).max(160),
    }),
    z.object({
        kind: z.literal('approval'),
        label: z.string().min(1).max(160),
    }),
]);
const transitions = {
    quotes: STATUS.quotes,
    jobs: STATUS.jobs,
    appointments: STATUS.appointments,
    invoices: STATUS.invoices,
};
export const LifecyclePolicyInput = z
    .object({
    key: z.string().regex(KEY_REGEX).max(60).optional(),
    name: z.string().min(1).max(120),
    description: z.string().max(1000).optional(),
    entity: z.enum(LIFECYCLE_POLICY_ENTITIES),
    to_status: z.string().min(1).max(40),
    mode: z.enum(LIFECYCLE_POLICY_MODES).default('enforce'),
    when: z.array(LifecyclePolicyCondition).max(10).default([]),
    requirements: z.array(LifecyclePolicyRequirement).min(1).max(20),
})
    .superRefine((policy, ctx) => {
    if (!transitions[policy.entity].includes(policy.to_status)) {
        ctx.addIssue({
            code: 'custom',
            path: ['to_status'],
            message: `Status "${policy.to_status}" is not valid for ${policy.entity}. Use: ${transitions[policy.entity].join(', ')}.`,
        });
    }
    if (policy.requirements.some((r) => r.kind === 'appointment_count') &&
        policy.entity !== 'jobs') {
        ctx.addIssue({
            code: 'custom',
            path: ['requirements'],
            message: 'appointment_count is currently supported only for job policies.',
        });
    }
});
export const LIFECYCLE_POLICY_GUIDE = `
LIFECYCLE POLICY FORMAT

Policies add requirements to an existing safe lifecycle transition. They never add an otherwise-invalid status transition or bypass accounting, permissions, tenant isolation, or audit controls.

- entity: quotes | jobs | appointments | invoices
- to_status: a valid status for that entity
- mode: enforce blocks the transition; observe reports missing evidence without blocking
- when: optional AND conditions. Use {kind:"total", min:5000} or {kind:"field_equals", path:"fields.work_type", value:"restoration"}.
- requirements:
  - {kind:"field_present", path:"service_location_id", label:"Service location is required"}
  - {kind:"field_present", path:"fields.customer_signed", label:"Customer signature is required"}
  - {kind:"document_count", document_kind:"completion_photo", min:2, label:"Two completion photos are required"}
  - {kind:"appointment_count", statuses:["done"], min:2, label:"Two visits must be completed"} (jobs only)
  - {kind:"approval", label:"Manager approval is required"}

Save creates or replaces one draft for a stable key. Simulate it against real records, then publish that exact version. Publishing archives the prior active version for the key; approvals are scoped to a policy version and record.
`;
//# sourceMappingURL=lifecycle-policies.js.map